import { Center, Loader } from '@mantine/core';
import { useAdminMe, useSession } from '../lib/session';
import { AdminAppShell } from './AppShell';
import { Login, NoAccess } from './Login';
import { LoadFailed } from './LoadFailed';

/**
 * The route guard for everything under the admin shell. NoAccess — the
 * designed reject screen — is reachable only from an affirmative 403.
 * Everything else (rate limited, server error, network failure, offline) is a
 * distinct, retryable LoadFailed state: none of those are a verdict on the
 * account's access, and telling an admin they lack access because of a
 * transient failure would be actively misleading.
 */
export function RequireAdmin() {
  const { session, loading } = useSession();
  const me = useAdminMe(Boolean(session));

  if (loading) {
    return (
      <Center h="100vh">
        <Loader data-testid="loader" />
      </Center>
    );
  }
  if (!session) return <Login />;
  if (me.error?.status === 401) return <Login />;
  if (me.error?.status === 403) return <NoAccess email={session.user.email ?? 'unknown'} />;

  // fetchStatus === 'paused' covers the offline case: TanStack Query pauses the
  // fetch instead of running it, so isLoading (isPending && isFetching) is false
  // and there is no error to branch on either — without this check that falls
  // straight through to "load failed" with no way to tell it apart from a real
  // failure once back online, or to NoAccess if left unhandled entirely.
  if (me.fetchStatus === 'paused' || me.isLoading) {
    return (
      <Center h="100vh">
        <Loader data-testid="loader" />
      </Center>
    );
  }
  if (me.isError || !me.data) {
    return <LoadFailed error={me.error} onRetry={() => void me.refetch()} />;
  }

  return <AdminAppShell me={me.data} />;
}
